import React from 'react';
import { Link } from 'react-router-dom';

import type {
  CodexRun,
  ConversationAttachment,
  ConversationMessage,
  MessageStatus,
  QueueItem,
  RunActivityTree,
  StatusItem
} from '../types';
import { MarkdownText } from '../utils/conversationRunActivity';
import {
  resolveQueuedTurnControls,
  resolveTurnStatus,
  type QueuedTurnControls
} from '../utils/turnStatus';
import { Dialog } from './Dialog';
import { Timestamp } from './Timestamp';

type LivePreview = {
  thinking?: string;
  command?: string;
  label?: string;
  text?: string;
};

type QueueOrder = {
  index: number;
  total: number;
};

const countCompactions = (tree: RunActivityTree | null | undefined): number =>
  tree
    ? tree.stages.reduce(
        (sum, stage) =>
          sum +
          stage.commands.filter(
            (command) =>
              command.stage.toLowerCase() === 'compaction' ||
              command.step.toLowerCase().includes('compaction') ||
              command.output_tail.some((line) => line.text.toLowerCase().includes('compaction'))
          ).length,
        0
      )
    : 0;

type ConversationMessageListProps = {
  displayMessages: ConversationMessage[];
  assignedAssistantIds: Set<string>;
  queueByMessageId: Map<string, QueueItem>;
  runByMessageId: Map<string, CodexRun>;
  assistantByUserId: Map<string, ConversationMessage>;
  streamingRun: CodexRun | null;
  assistantText: string;
  activeRunActivityTree: RunActivityTree | null;
  activityTreeByMessageId: Record<string, RunActivityTree | null | undefined>;
  activeLivePreview: LivePreview | null;
  runnerStateMessage: string;
  queueSpanByMessageId: Record<string, string>;
  queueOrderByMessageId: Map<string, QueueOrder>;
  autopilotMessageIds: Set<string>;
  messagePreviewById: Map<string, string>;
  selectedActivityMessageId: string | null;
  queueReorderBusy: boolean;
  editingMessageId: string | null;
  editingContent: string;
  editingBusy: boolean;
  actorUserId: string | null;
  canRespond: boolean;
  canManageConversation: boolean;
  canInspectRuntime: boolean;
  canReadArtifacts: boolean;
  canPublishArtifacts: boolean;
  onEditingContentChange: (value: string) => void;
  onEditKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onCancelEditing: () => void;
  onSaveEditedMessage: (messageId: string) => void;
  onCopyMessageContent: (content: string) => void;
  onOpenFixBug: (messageId: string, queueSpanId: string) => void;
  onOpenMessageActivitySidebar: (messageId: string, runId?: string | null) => void;
  onMoveQueuedMessage: (messageId: string, direction: 'up' | 'down') => void;
  onToggleQueuedMessageHold: (itemId: string, manualHold: boolean) => void;
  onStartQueuedMessage: (itemId: string) => void;
  onCancelQueue: (itemId: string) => void;
  onAllowPipelineWait: (itemId: string) => void;
  onStopPipelineWait: (itemId: string) => void;
  onStartEditingMessage: (message: ConversationMessage) => void;
  onPreviewAttachment: (attachment: ConversationAttachment) => void;
  onDownloadAttachment: (attachment: ConversationAttachment) => void;
  onDeleteAttachment: (attachment: ConversationAttachment) => void;
  messageStatusBadgeClass: (status: MessageStatus, queueItem?: QueueItem | null) => string;
  messageStatusLabel: (status: MessageStatus, queueItem?: QueueItem | null) => string;
};

type PendingCancelConfirmation = {
  itemId: string;
  messagePreview: string;
};

function CopyButton({
  onClick
}: {
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="message-copy-btn"
      onClick={onClick}
      title="Copy message"
      aria-label="Copy message"
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
      </svg>
    </button>
  );
}

function formatAttachmentSize(bytes: number) {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function AttachmentList({
  attachments,
  onPreview,
  onDownload,
  onDelete,
  canRead,
  canDelete
}: {
  attachments?: ConversationAttachment[];
  onPreview: (attachment: ConversationAttachment) => void;
  onDownload: (attachment: ConversationAttachment) => void;
  onDelete: (attachment: ConversationAttachment) => void;
  canRead: boolean;
  canDelete: (attachment: ConversationAttachment) => boolean;
}) {
  if (!attachments || attachments.length === 0) {
    return null;
  }
  return (
    <div className="message-attachments">
      {attachments.map((attachment) => (
        <div className="message-attachment" key={attachment.id}>
          <div className="message-attachment__body">
            <span className="message-attachment__name">{attachment.filename}</span>
            <span className="message-attachment__meta">
              {formatAttachmentSize(attachment.byte_size)} · {attachment.workspace_path}
            </span>
          </div>
          <div className="message-attachment__actions">
            {canRead && <>
              <button type="button" className="ghost compact" onClick={() => onPreview(attachment)}>
                Preview
              </button>
              <button type="button" className="ghost compact" onClick={() => onDownload(attachment)}>
                Download
              </button>
            </>}
            {canDelete(attachment) && <button type="button" className="secondary compact" onClick={() => onDelete(attachment)}>
              Delete
            </button>}
          </div>
        </div>
      ))}
    </div>
  );
}

function StatusItemsList({ items }: { items: StatusItem[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="status-items">
      {items.map((item, index) => (
        <li key={index} className={`status-item status-item--${item.severity}`}>
          {item.message}
        </li>
      ))}
    </ul>
  );
}

function renderTimelineParts(
  messageQueue: QueueItem | undefined,
  queueControls: QueuedTurnControls | null,
  messageRun: CodexRun | undefined,
  messagePreviewById: Map<string, string>
): React.ReactNode[] {
  const timelineParts: React.ReactNode[] = [];
  if (messageQueue?.created_at) {
    timelineParts.push(
      <span key="queued">
        Queued <Timestamp value={messageQueue.created_at} />
      </span>
    );
  }
  if (queueControls?.manualHold) {
    timelineParts.push(<span key="manual-hold">Paused until started manually</span>);
  }
  if (
    messageQueue &&
    queueControls &&
    !queueControls.manualHold &&
    messageQueue.waiting_for_count > 0
  ) {
    timelineParts.push(
      <span key="waiting">
        {`Waiting for ${messageQueue.waiting_for_count} ${
          messageQueue.waiting_for_count === 1 ? 'earlier turn' : 'earlier turns'
        }`}
      </span>
    );
    const blockers = messageQueue.waiting_for_message_ids ?? [];
    if (blockers.length > 0) {
      const labels = blockers
        .slice(0, 3)
        .map((blockerId) => messagePreviewById.get(blockerId) ?? blockerId);
      const remaining = blockers.length - labels.length;
      timelineParts.push(
        <span key="blocking">
          Blocking: {labels.join(' | ')}
          {remaining > 0 ? ` (+${remaining} more)` : ''}
        </span>
      );
    }
  }
  if (messageRun?.started_at) {
    timelineParts.push(
      <span key="started">
        Started <Timestamp value={messageRun.started_at} />
      </span>
    );
  }
  if (messageRun?.finished_at) {
    const label =
      messageRun.status === 'error'
        ? 'Failed'
        : messageRun.status === 'canceled'
          ? 'Canceled'
          : 'Finished';
    timelineParts.push(
      <span key="finished">
        {label} <Timestamp value={messageRun.finished_at} />
      </span>
    );
  }
  return timelineParts;
}

function AgentRunMeta({ run }: { run: CodexRun | undefined }) {
  if (!run) {
    return null;
  }
  if (run.status === 'running') {
    return <span className="badge badge--processing">live</span>;
  }
  if (run.status === 'error') {
    return <span className="badge badge--error">failed</span>;
  }
  if (run.status === 'canceled') {
    return <span className="badge badge--warning">canceled</span>;
  }
  if (run.finished_at) {
    return <Timestamp value={run.finished_at} />;
  }
  return <span className="badge">{run.status}</span>;
}

function AgentLivePreview({
  livePreview
}: {
  livePreview: LivePreview;
}) {
  return (
    <div className="message-live-preview" role="status" aria-live="polite">
      {livePreview.thinking && (
        <div className="message-live-preview__section">
          <div className="message-live-preview__label">Thinking</div>
          <MarkdownText
            className="message-live-preview__text message-live-preview__text--markdown"
            text={livePreview.thinking}
          />
        </div>
      )}
      {livePreview.command && (
        <div className="message-live-preview__section">
          <div className="message-live-preview__label">Command</div>
          <div className="message-live-preview__text message-live-preview__text--command">
            {livePreview.command}
          </div>
        </div>
      )}
      {!livePreview.thinking && !livePreview.command && livePreview.label && (
        <div className="message-live-preview__section">
          <div className="message-live-preview__label">{livePreview.label}</div>
          <MarkdownText
            className="message-live-preview__text message-live-preview__text--markdown"
            text={livePreview.text ?? ''}
          />
        </div>
      )}
    </div>
  );
}

export function ConversationMessageList({
  displayMessages,
  assignedAssistantIds,
  queueByMessageId,
  runByMessageId,
  assistantByUserId,
  streamingRun,
  assistantText,
  activeRunActivityTree,
  activityTreeByMessageId,
  activeLivePreview,
  runnerStateMessage,
  queueSpanByMessageId,
  queueOrderByMessageId,
  autopilotMessageIds,
  messagePreviewById,
  selectedActivityMessageId,
  queueReorderBusy,
  editingMessageId,
  editingContent,
  editingBusy,
  actorUserId,
  canRespond,
  canManageConversation,
  canInspectRuntime,
  canReadArtifacts,
  canPublishArtifacts,
  onEditingContentChange,
  onEditKeyDown,
  onCancelEditing,
  onSaveEditedMessage,
  onCopyMessageContent,
  onOpenFixBug,
  onOpenMessageActivitySidebar,
  onMoveQueuedMessage,
  onToggleQueuedMessageHold,
  onStartQueuedMessage,
  onCancelQueue,
  onAllowPipelineWait,
  onStopPipelineWait,
  onStartEditingMessage,
  onPreviewAttachment,
  onDownloadAttachment,
  onDeleteAttachment,
  messageStatusBadgeClass,
  messageStatusLabel
}: ConversationMessageListProps) {
  const [pendingCancelConfirmation, setPendingCancelConfirmation] =
    React.useState<PendingCancelConfirmation | null>(null);
  const canDeleteAttachment = React.useCallback(
    (attachment: ConversationAttachment) =>
      canPublishArtifacts &&
      (canManageConversation ||
        (Boolean(actorUserId) && attachment.user_id === actorUserId)),
    [actorUserId, canManageConversation, canPublishArtifacts]
  );

  function pipelineWaitAction(message: ConversationMessage): string | null {
    return (
      message.status_items?.find((item) => item.code === 'pipeline_wait_guard')?.action ?? null
    );
  }

  function handleCancelQueue(queueControls: QueuedTurnControls, message: ConversationMessage) {
    if (pipelineWaitAction(message) === 'stop_pipeline_wait') {
      onStopPipelineWait(queueControls.itemId);
      return;
    }
    if (queueControls.cancelRequiresConfirmation) {
      setPendingCancelConfirmation({
        itemId: queueControls.itemId,
        messagePreview: message.content.trim().slice(0, 180)
      });
      return;
    }
    onCancelQueue(queueControls.itemId);
  }

  function confirmProcessingCancel() {
    if (!pendingCancelConfirmation) {
      return;
    }
    onCancelQueue(pendingCancelConfirmation.itemId);
    setPendingCancelConfirmation(null);
  }

  return (
    <>
      {displayMessages.map((message) => {
        if (message.role !== 'user') {
          if (message.role === 'assistant' && assignedAssistantIds.has(message.id)) {
            return null;
          }
          return (
            <div key={message.id} className="message-bubble message-bubble--assistant">
              <div className="message-meta">
                <span>Agent</span>
                <Timestamp value={message.created_at} />
                {message.status !== 'done' && <span className="badge">{message.status}</span>}
                <CopyButton onClick={() => onCopyMessageContent(message.content)} />
              </div>
              <div className="message-content">{message.content}</div>
              <AttachmentList
                attachments={message.attachments}
                onPreview={onPreviewAttachment}
                onDownload={onDownloadAttachment}
                onDelete={onDeleteAttachment}
                canRead={canReadArtifacts}
                canDelete={canDeleteAttachment}
              />
              {message.status === 'error' && message.error && (
                <div className="message-error">{message.error}</div>
              )}
              <StatusItemsList items={message.status_items ?? []} />
            </div>
          );
        }

        const messageQueue = queueByMessageId.get(message.id);
        const messageRun = runByMessageId.get(message.id);
        const assistantMessage = assistantByUserId.get(message.id) ?? null;
        const currentPipelineWaitAction = pipelineWaitAction(message);
        const isStreamingMessage = streamingRun?.message_id === message.id;
        const liveText = isStreamingMessage ? assistantText : '';
        const hasLiveText = isStreamingMessage && liveText.trim().length > 0;
        const isShellRun = messageRun?.kind === 'shell';
        const statusLabel = resolveTurnStatus({
          messageStatus: message.status,
          queueStatus: messageQueue?.status ?? null,
          runStatus: messageRun?.status ?? null,
          assistantStatus: assistantMessage?.status ?? null
        });
        const queueControls = resolveQueuedTurnControls({
          messageId: message.id,
          turnStatus: statusLabel,
          queueItem: messageQueue
        });
        const showStatus = statusLabel && statusLabel !== 'done';
        const isQueuedMessage = statusLabel === 'queued';
        const isMessageProcessing =
          statusLabel === 'processing' || messageRun?.status === 'running';
        const queueError = messageQueue?.error ?? null;
        const isAutopilotMessage = autopilotMessageIds.has(message.id);
        const canMutateTurn =
          canManageConversation ||
          (canRespond && Boolean(actorUserId) && message.user_id === actorUserId);
        const canEdit =
          canMutateTurn &&
          (['queued', 'canceled', 'error'].includes(statusLabel) ||
            (isAutopilotMessage && statusLabel === 'done'));
        const isEditing = canMutateTurn && editingMessageId === message.id;
        const runError = messageRun?.status === 'error' ? messageRun.error ?? null : null;
        const errorText = statusLabel === 'error' ? message.error ?? queueError ?? runError : null;
        const shouldShowOutput = Boolean(messageRun);
        const treeForBadge =
          isStreamingMessage && activeRunActivityTree
            ? activeRunActivityTree
            : activityTreeByMessageId[message.id];
        const cachedOutputCount = treeForBadge
          ? treeForBadge.stages.reduce(
              (stageAcc, stage) =>
                stageAcc + stage.commands.reduce((cmdAcc, cmd) => cmdAcc + cmd.output_count, 0),
              0
            )
          : 0;
        const compactionCount = countCompactions(treeForBadge);
        const responseText = assistantMessage?.status === 'done' ? assistantMessage.content : '';
        const livePreview = isStreamingMessage ? activeLivePreview : null;
        const showLivePreview = Boolean(livePreview) && !responseText.trim();
        const activityChipLabel = (() => {
          if (isMessageProcessing) {
            const latest = isStreamingMessage ? runnerStateMessage.trim() : '';
            if (latest) {
              return latest;
            }
            return 'Working…';
          }
          if (statusLabel === 'queued') {
            return messageQueue?.manual_hold ? 'Paused in queue' : 'Queued and waiting';
          }
          return 'Turn activity';
        })();
        const showActivitySpinner = isMessageProcessing;
        const showAgentBubble =
          Boolean(messageRun || assistantMessage || hasLiveText || showLivePreview);
        const queueSpanId = messageQueue?.id ?? queueSpanByMessageId[message.id] ?? '';
        const logsLink = queueSpanId
          ? `/logs?span_id=${encodeURIComponent(queueSpanId)}`
          : `/logs?q=${encodeURIComponent(message.id)}`;
        const canShowUserTurnActivityShortcut =
          !showAgentBubble &&
          (Boolean(messageRun) || isQueuedMessage || isMessageProcessing || Boolean(treeForBadge));
        const queueOrder = queueOrderByMessageId.get(message.id) ?? null;
        const timelineParts = renderTimelineParts(
          messageQueue,
          queueControls,
          messageRun,
          messagePreviewById
        );

        return (
          <React.Fragment key={message.id}>
            <div
              className={`message-bubble ${
                isQueuedMessage ? 'message-bubble--queued' : 'message-bubble--user'
              }`}
            >
              <div className="message-meta">
                <span>You</span>
                <Timestamp value={message.created_at} />
                <span className="message-id" title={`Message ID: ${message.id}`}>
                  ID {message.id}
                </span>
                <CopyButton onClick={() => onCopyMessageContent(message.content)} />
                {showStatus && (
                  <span className={messageStatusBadgeClass(statusLabel, messageQueue ?? null)}>
                    {messageStatusLabel(statusLabel, messageQueue ?? null)}
                    {isMessageProcessing && <span className="spinner-inline" />}
                  </span>
                )}
                {isQueuedMessage && queueOrder && queueOrder.total > 1 && (
                  <span className="badge">
                    Queue {queueOrder.index + 1}/{queueOrder.total}
                  </span>
                )}
              </div>
              {isEditing ? (
                <div className="stack">
                  <textarea
                    value={editingContent}
                    onChange={(event) => onEditingContentChange(event.target.value)}
                    rows={4}
                    onKeyDown={onEditKeyDown}
                  />
                  <div className="row">
                    <button
                      type="button"
                      disabled={editingBusy}
                      onClick={() => onSaveEditedMessage(message.id)}
                    >
                      {editingBusy ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="secondary"
                      onClick={onCancelEditing}
                      disabled={editingBusy}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="message-content">{message.content}</div>
              )}
              <AttachmentList
                attachments={message.attachments}
                onPreview={onPreviewAttachment}
                onDownload={onDownloadAttachment}
                onDelete={onDeleteAttachment}
                canRead={canReadArtifacts}
                canDelete={canDeleteAttachment}
              />
              {errorText ? (
                isEditing ? (
                  <details className="message-error message-error--details">
                    <summary>Last error (click to expand)</summary>
                    <pre>{errorText}</pre>
                  </details>
                ) : (
                  <div className="message-error">{errorText}</div>
                )
              ) : null}
              {timelineParts.length > 0 && (
                <div className="message-timeline">{timelineParts}</div>
              )}
              {canInspectRuntime &&
                messageQueue &&
                queueControls &&
                !queueControls.manualHold &&
                (messageQueue.waiting_for_message_ids?.length ?? 0) > 0 && (
                  <div className="message-timeline">
                    Blocking messages:{' '}
                    {messageQueue.waiting_for_message_ids!.map((blockingMessageId, index) => (
                      <React.Fragment key={blockingMessageId}>
                        {index > 0 ? ' · ' : ''}
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => onOpenMessageActivitySidebar(blockingMessageId)}
                        >
                          {blockingMessageId}
                        </button>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              <div className="row">
                {canInspectRuntime && (
                  <Link className="link-button" to={logsLink}>
                    Logs
                  </Link>
                )}
                {canManageConversation && (
                  <button
                    type="button"
                    className="link-button secondary"
                    onClick={() => onOpenFixBug(message.id, queueSpanId)}
                  >
                    Fix bug
                  </button>
                )}
                {canInspectRuntime && canShowUserTurnActivityShortcut && (
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => onOpenMessageActivitySidebar(message.id, messageRun?.id)}
                    title={activityChipLabel}
                  >
                    Turn activity
                    {showActivitySpinner && <span className="spinner-inline" />}
                    {cachedOutputCount > 0 && <span className="badge">{cachedOutputCount}</span>}
                    {compactionCount > 0 && <span className="badge">compact {compactionCount}</span>}
                  </button>
                )}
                {canManageConversation && messageQueue?.status === 'queued' && queueOrder && queueOrder.total > 1 && (
                  <>
                    <button
                      type="button"
                      className="ghost"
                      disabled={queueReorderBusy || queueOrder.index === 0}
                      onClick={() => onMoveQueuedMessage(message.id, 'up')}
                    >
                      Move up
                    </button>
                    <button
                      type="button"
                      className="ghost"
                      disabled={queueReorderBusy || queueOrder.index === queueOrder.total - 1}
                      onClick={() => onMoveQueuedMessage(message.id, 'down')}
                    >
                      Move down
                    </button>
                  </>
                )}
                {canMutateTurn && queueControls?.showHoldToggle && (
                  <label className="checkbox-row" title="Keep this message in the queue">
                    <input
                      type="checkbox"
                      checked={queueControls.manualHold}
                      onChange={(event) =>
                        onToggleQueuedMessageHold(queueControls.itemId, event.target.checked)
                      }
                    />
                    <span>Hold</span>
                  </label>
                )}
                {canMutateTurn && queueControls?.showStartNow && (
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => onStartQueuedMessage(queueControls.itemId)}
                  >
                    Start now
                  </button>
                )}
                {canMutateTurn && queueControls?.showCancel && (
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => handleCancelQueue(queueControls, message)}
                  >
                    {currentPipelineWaitAction === 'stop_pipeline_wait'
                      ? 'Stop waiting'
                      : isQueuedMessage
                        ? 'Remove'
                        : 'Stop run'}
                  </button>
                )}
                {canMutateTurn && queueControls && currentPipelineWaitAction === 'allow_pipeline_wait' && (
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => onAllowPipelineWait(queueControls.itemId)}
                  >
                    Allow pipeline wait
                  </button>
                )}
                {canEdit && !isEditing && (
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => onStartEditingMessage(message)}
                  >
                    Edit & retry
                  </button>
                )}
              </div>
            </div>
            {showAgentBubble && (
              <div className="message-bubble message-bubble--assistant">
                <div className="message-meta">
                  <span className="message-meta__agent">
                    {messageRun?.agent_name ?? (isShellRun ? 'Shell' : 'Agent')}
                  </span>
                  {assistantMessage?.created_at ? (
                    <Timestamp value={assistantMessage.created_at} />
                  ) : (
                    <AgentRunMeta run={messageRun} />
                  )}
                  {responseText.trim() && (
                    <CopyButton onClick={() => onCopyMessageContent(responseText)} />
                  )}
                </div>
                {canInspectRuntime && shouldShowOutput && (
                  <div className="message-actions">
                    <button
                      type="button"
                      className={`message-actions__btn ${
                        selectedActivityMessageId === message.id
                          ? 'message-actions__btn--active'
                          : ''
                      } ${showActivitySpinner ? 'message-actions__btn--live' : ''}`}
                      onClick={() => onOpenMessageActivitySidebar(message.id, messageRun?.id)}
                      title={activityChipLabel}
                    >
                      <span className="message-actions__label">{activityChipLabel}</span>
                      {showActivitySpinner && <span className="spinner-inline" />}
                      {cachedOutputCount > 0 && (
                        <span className="message-actions__badge">{cachedOutputCount}</span>
                      )}
                      {compactionCount > 0 && (
                        <span className="message-actions__badge">compact {compactionCount}</span>
                      )}
                    </button>
                  </div>
                )}
                {showLivePreview && livePreview && <AgentLivePreview livePreview={livePreview} />}
                {responseText.trim() && <div className="message-content">{responseText}</div>}
                <AttachmentList
                  attachments={assistantMessage?.attachments}
                  onPreview={onPreviewAttachment}
                  onDownload={onDownloadAttachment}
                  onDelete={onDeleteAttachment}
                  canRead={canReadArtifacts}
                  canDelete={canDeleteAttachment}
                />
                <StatusItemsList items={assistantMessage?.status_items ?? []} />
              </div>
            )}
          </React.Fragment>
        );
      })}
      <Dialog
        open={Boolean(pendingCancelConfirmation)}
        title="Stop running turn?"
        size="small"
        onClose={() => setPendingCancelConfirmation(null)}
        footer={
          <>
            <button
              type="button"
              className="secondary"
              onClick={() => setPendingCancelConfirmation(null)}
            >
              Keep running
            </button>
            <button type="button" className="danger" onClick={confirmProcessingCancel}>
              Stop turn
            </button>
          </>
        }
      >
        <div className="dialog-body cancel-turn-confirm">
          <p>
            This turn is already processing. Stopping it cancels the active run and any in-flight
            command output.
          </p>
          {pendingCancelConfirmation?.messagePreview && (
            <div className="cancel-turn-confirm__preview">
              {pendingCancelConfirmation.messagePreview}
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
}
